// Mirrors the DTOs in Modules/MailOcr/Models/*.cs — keep in sync by hand.

export interface QueueRowDto {
  id: string;
  fileName: string | null;
  clientName: string;
  status: string;
  processingStage: string | null;
  receivedAt: string;
  businessTaxId: string | null;
  invoiceNumber: string | null;
  invoiceTotal: number | null;
  overallConfidence: number | null;
  hasOpenGenieIssue: boolean;
  wasEdited: boolean;
  /** The login of the Alii user the document belongs to: its uploader, or the client's user who sent the mail. */
  userName: string | null;
  userDisplayName: string | null;
}

export interface QueueCountsDto {
  all: number;
  needsReview: number;
  failed: number;
  rejected: number;
  completed: number;
}

export interface QueueResponseDto {
  items: QueueRowDto[];
  nextCursor: string | null;
  counts: QueueCountsDto;
}

export interface QueueFilters {
  status: string;
  dateRange: string;
  search: string;
  statuses: string[];
  clientIds: number[];
  businessTaxIds: string[];
  fileTypes: string[];
  confidence: string[];
  sort: string;
}

export const DEFAULT_QUEUE_FILTERS: QueueFilters = {
  status: 'all',
  dateRange: 'today',
  search: '',
  statuses: [],
  clientIds: [],
  businessTaxIds: [],
  fileTypes: [],
  confidence: [],
  sort: 'newest',
};

export interface DocumentFieldDto {
  fieldName: string;
  ocrValue: string | null;
  confidence: number | null;
  valueInAlii: string | null;
  changedBy: string | null;
}

export interface LineItemDto {
  lineNumber: number;
  itemCode: string | null;
  description: string | null;
  quantity: number | null;
  unitCost: number | null;
  tax: number | null;
  total: number | null;
}

export interface PromptLayerDto {
  instruction: string | null;
  exists: boolean;
  promptId: string | null;
}

export interface FieldPromptDto {
  fieldName: string;
  global: PromptLayerDto;
  client: PromptLayerDto | null;
  clientBusinessTaxId: PromptLayerDto | null;
  effectiveInstruction: string;
  effectiveSource: string;
  effectiveVersion: number | null;
}

export interface ActivityDto {
  timestamp: string;
  eventType: string;
  actor: string | null;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  detail: string | null;
}

export interface DocumentDetailDto {
  id: string;
  fileName: string | null;
  clientName: string;
  clientId: number;
  businessTaxId: string | null;
  status: string;
  processingStage: string | null;
  createdAt: string;
  updatedAt: string;
  errorMessage: string | null;
  rejectionReason: string | null;
  hasPdf: boolean;
  canApprove: boolean;
  canUnreject: boolean;
  hasOpenGenieIssue: boolean;
  fields: DocumentFieldDto[];
  lineItems: LineItemDto[];
  prompts: FieldPromptDto[];
  activities: ActivityDto[];
  exportedAt: string | null;
  aliiInvoiceKey: string | null;
  aliiInvoiceUrl: string | null;
  canExport: boolean;
  /** Whether a user uploaded the file, rather than it being mailed in. */
  isUpload: boolean;
  /** The login of the Alii user the document belongs to: its uploader, or the client's user who sent the mail. */
  userName: string | null;
  userDisplayName: string | null;
  /** The document's Invoice Genie issue, when one was raised after its values reached Alii. */
  genieIssue: DocumentGenieIssueDto | null;
}

export interface DocumentGenieIssueDto {
  id: string;
  status: string;
  note: string | null;
  reportedDate: string;
  /** Who set it to Fixed or Ignored, while it is. */
  editedBy: string | null;
  findings: GenieFindingDto[];
  /** One tag per source and field. A null field name means Invoice Genie flagged the whole invoice. */
  sources: GenieIssueSourceDto[];
  /** OCR values a reviewer corrected before the invoice was sent to Alii. */
  reviewCorrections: GenieValueChangeDto[];
  /** OCR values a user changed in Alii Main after the invoice was sent. */
  aliiEdits: GenieValueChangeDto[];
  /** Every field the issue is about, to highlight. */
  fieldNames: string[];
}

export interface GenieIssueSourceDto {
  source: string;
  fieldName: string | null;
  isResolved: boolean;
}

export interface GenieValueChangeDto {
  fieldName: string;
  ocrValue: string | null;
  value: string | null;
  changedBy: string | null;
  changedAt: string;
}

export interface GenieFindingDto {
  message: string;
  fieldNames: string[];
  reportedBy: string | null;
  reportedDate: string;
  isResolved: boolean;
}

export interface ClientDto {
  id: number;
  name: string;
}

export interface FieldRowDto {
  fieldName: string;
  instruction: string;
  source: string;
  promptId: string | null;
}

export interface PromptVersionDto {
  id: string;
  versionNumber: number;
  instruction: string;
  editedBy: string | null;
  createdAt: string;
}

export interface RejectPromptDto {
  promptId: string | null;
  instruction: string;
  exists: boolean;
}

export interface ConfidenceThresholdDto {
  minConfidence: number | null;
  requireReview: boolean;
  isOverride: boolean;
}

export interface TrialResponseDto {
  value: string | null;
  confidence: number | null;
}

export interface InvoiceGenieIssueDto {
  id: string;
  supplierName: string;
  businessTaxId: string | null;
  source: string[];
  ocrDate: string;
  reportedDate: string;
  status: string;
  editedBy: string | null;
  note: string | null;
  documentId: string;
  highlightedFields: string[];
}

export interface FailedMailAttachmentDto {
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
}

export interface FailedMailDto {
  id: string;
  receivedAt: string;
  toAddress: string;
  matchedClient: string;
  subject: string;
  fromAddress: string;
  reasonTag: string;
  attachments: FailedMailAttachmentDto[];
  dismissed: boolean;
}

export interface FailedMailsResponseDto {
  items: FailedMailDto[];
  nextCursor: string | null;
}

/** A bar on an Insights chart: a field name (see FIELD_LABELS) or an Invoice Genie rule (see GENIE_RULE_LABELS). */
export interface InsightsBarDto {
  key: string;
  value: number;
}

/** Counts for the documents received since receivedFrom; the page works out the percentages. */
export interface InsightsResponseDto {
  receivedFrom: string;
  processed: number;
  autoAccepted: number;
  corrected: number;
  failed: number;
  genieIssues: number;
  correctionsByField: InsightsBarDto[];
  issuesByRule: InsightsBarDto[];
}

/** Invoice Genie rules, as the OCR service names them. */
export const GENIE_RULE_LABELS: Record<string, string> = {
  InvoiceNumber: 'Invoice number',
  InvoiceDate: 'Invoice date',
  InvoiceAmount: 'Invoice amount',
  SupplierBusinessTaxId: 'Supplier business tax ID',
  SupplierBankBranchCode: 'Supplier bank branch code',
  SupplierAccountNumber: 'Supplier account number',
  PoNumber: 'PO number',
  WholeDocument: 'Whole document',
  LowConfidence: 'Low confidence',
};

export const FIELD_LABELS: Record<string, string> = {
  BusinessTaxId: 'Business tax ID',
  InvoiceNumber: 'Invoice number',
  InvoiceDate: 'Invoice date',
  PoNumber: 'PO number',
  TaxAmount: 'Tax amount',
  InvoiceTotal: 'Total',
  BankBranchCode: 'Bank branch code',
  AccountNumber: 'Account number',
};

export const TRANSIENT_STATUSES = ['Queued for mail extraction', 'Processing'];

export const ISSUE_STATUSES = ['New', 'Investigating', 'Fixed', 'Ignored'];

/** What raised an Invoice Genie issue, as the admin API names it. */
export const GENIE_SOURCES = {
  genie: 'Invoice Genie flagged',
  userEdited: 'User edited',
  corrected: 'Corrected',
};

export const MAIL_OCR_ROUTES = {
  queue: '/mail-ocr/queue',
  document: (id: string, highlight: string[] = []) => `/mail-ocr/queue/document?id=${id}${highlight.length ? `&highlight=${highlight.join(',')}` : ''}`,
  invoiceGenie: '/mail-ocr/invoice-genie',
  /** A document's Invoice Genie issue. A document has at most one, so it is opened by the document's id. */
  genieIssue: (documentId: string) => `/mail-ocr/invoice-genie/issue?id=${documentId}`,
  failedMails: '/mail-ocr/failed-mails',
  failedMail: (id: string) => `/mail-ocr/failed-mails/detail?id=${id}`,
  insights: '/mail-ocr/insights',
  rules: '/mail-ocr/rules',
};
